import { useEffect, useState, useMemo } from 'react';
import { Package, Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useProducts, useAddToWarehouse } from '@/hooks/warehouse';
import { useOrganizations } from '@/hooks/useOrganizations';
import { getThumbnailImageUrl } from '@/lib/cloudinary';
import { cn } from '@/lib/utils';

interface AddToWarehouseModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddToWarehouseModal = ({ isOpen, onClose }: AddToWarehouseModalProps) => {
  const [productId, setProductId] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [minStockAlert, setMinStockAlert] = useState('');
  const [notes, setNotes] = useState('');
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  const { data: products = [] } = useProducts();
  const { data: organizations = [] } = useOrganizations();
  const { mutate: addToWarehouse, isPending } = useAddToWarehouse();

  // Filter products based on search
  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return products;
    const searchLower = productSearch.toLowerCase();
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(searchLower) ||
        (p.description && p.description.toLowerCase().includes(searchLower))
    );
  }, [products, productSearch]);

  // Get selected product
  const selectedProduct = products.find((p) => p.id === productId);

  // Update unit price when product is selected
  useEffect(() => {
    if (productId) {
      const product = products.find((p) => p.id === productId);
      if (product) {
        setUnitPrice(product.price.toString());
      }
    }
  }, [productId, products]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setProductId('');
      setOrganizationId('');
      setQuantity('');
      setUnitPrice('');
      setMinStockAlert('');
      setNotes('');
      setProductSearch('');
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!productId || !organizationId || !quantity || !unitPrice) return;

    addToWarehouse(
      {
        product_id: productId,
        organization_id: organizationId,
        quantity: parseInt(quantity, 10),
        unit_price: parseFloat(unitPrice),
        min_stock_alert: minStockAlert.trim() ? parseInt(minStockAlert, 10) : null,
        notes: notes.trim() || undefined,
      },
      { onSuccess: onClose }
    );
  };

  const truncateDescription = (desc: string | null, maxLength = 50) => {
    if (!desc) return '';
    return desc.length > maxLength ? `${desc.substring(0, maxLength)}...` : desc;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Agregar al Almacén</DialogTitle>
            <DialogDescription>
              Selecciona un producto del catálogo para agregar al almacén
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Product Search */}
            <div className="grid gap-2">
              <Label htmlFor="product">Producto</Label>
              <Popover open={productSearchOpen} onOpenChange={setProductSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={productSearchOpen}
                    className="w-full justify-between h-auto min-h-10"
                  >
                    {selectedProduct ? (
                      <div className="flex items-center gap-3">
                        {selectedProduct.image_url ? (
                          <img
                            src={getThumbnailImageUrl(selectedProduct.image_url)}
                            alt={selectedProduct.name}
                            className="h-8 w-8 rounded object-cover"
                          />
                        ) : (
                          <div className="h-8 w-8 rounded bg-muted flex items-center justify-center">
                            <Package className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                        <span className="truncate">{selectedProduct.name}</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">Buscar producto...</span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[450px] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Buscar producto..."
                      value={productSearch}
                      onValueChange={setProductSearch}
                    />
                    <CommandList>
                      <CommandEmpty>No se encontraron productos</CommandEmpty>
                      <CommandGroup>
                        {filteredProducts.map((product) => (
                          <CommandItem
                            key={product.id}
                            value={product.id}
                            onSelect={(value) => {
                              setProductId(value);
                              setProductSearchOpen(false);
                              setProductSearch('');
                            }}
                            className="flex items-center gap-3 py-2"
                          >
                            {product.image_url ? (
                              <img
                                src={getThumbnailImageUrl(product.image_url)}
                                alt={product.name}
                                className="h-10 w-10 rounded object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="h-10 w-10 rounded bg-muted flex items-center justify-center flex-shrink-0">
                                <Package className="h-5 w-5 text-muted-foreground" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{product.name}</p>
                              <p className="text-sm text-muted-foreground truncate">
                                {truncateDescription(product.description)}
                              </p>
                            </div>
                            <Check
                              className={cn(
                                "h-4 w-4 flex-shrink-0",
                                productId === product.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {products.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No hay productos en el catálogo. Crea uno primero.
                </p>
              )}
            </div>

            {/* Organization Select */}
            <div className="grid gap-2">
              <Label htmlFor="organization">Organización</Label>
              <Select value={organizationId} onValueChange={setOrganizationId}>
                <SelectTrigger className="bg-gray-100 hover:bg-gray-200 transition-colors">
                  <SelectValue placeholder="Selecciona una organización" />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="quantity">Cantidad</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="0"
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="unitPrice">Precio Unitario</Label>
                <Input
                  id="unitPrice"
                  type="number"
                  step="0.01"
                  min="0"
                  value={unitPrice}
                  onChange={(e) => setUnitPrice(e.target.value)}
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="minStockAlert">Alerta de Stock Mínimo (Opcional)</Label>
              <Input
                id="minStockAlert"
                type="number"
                min="0"
                value={minStockAlert}
                onChange={(e) => setMinStockAlert(e.target.value)}
                placeholder="Opcional"
              />
              <p className="text-xs text-muted-foreground">
                Dejar vacío para no recibir alertas de stock
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="notes">Notas (opcional)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notas adicionales..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending || !productId || !organizationId}>
              {isPending ? 'Agregando...' : 'Agregar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddToWarehouseModal;
